import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AlertCircle, ArrowRight } from 'lucide-react';

import LayoutAuth from '../components/layoutAuth';
import { authService } from '../services/auth';
import { getParameterByName } from '../utils/urls';

const inputClass =
  'mt-2 block w-full rounded-xl border border-white/10 bg-beachvision-elevated px-4 py-3 text-sm text-white placeholder:text-beachvision-muted focus:border-beachvision-teal focus:outline-none focus:ring-2 focus:ring-beachvision-teal/30';

function normalizeRegisterError(requestError) {
  const fallback = 'Could not create account. Please try again.';
  const data = requestError?.response?.data;

  if (!data) {
    return fallback;
  }
  if (typeof data?.detail === 'string') {
    return data.detail;
  }
  if (typeof data === 'string') {
    return data;
  }
  if (typeof data === 'object') {
    const firstField = Object.keys(data)[0];
    const value = data[firstField];
    if (Array.isArray(value) && value[0]) {
      return String(value[0]);
    }
    if (typeof value === 'string') {
      return value;
    }
  }

  return fallback;
}

const Register = ({ authenticated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (authenticated) {
    return <Navigate to="/dashboard/" replace />;
  }

  const submitForm = (e) => {
    e.preventDefault();
    setError(null);

    const email = e.target.elements.email.value.trim();
    const password = e.target.elements.password.value;
    const confirmPassword = e.target.elements.confirmPassword.value;
    const code = e.target.elements.code.value.trim();

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    authService.register(
      {
        email,
        username: email,
        password,
        code,
      },
      () => {
        authService.fetchUser(
          () => {
            const next = getParameterByName('next', window.location);
            window.location = next || '/dashboard/';
          },
          () => {
            console.error('Failed to fetch user info');
          }
        );
      },
      (requestError) => {
        setError(normalizeRegisterError(requestError));
        setLoading(false);
      }
    );
  };

  return (
    <LayoutAuth
      title="Create account"
      subtitle="Start tracking your game and get instant session analysis."
    >
      <form className="space-y-5" action="#" onSubmit={submitForm}>
        {error ? (
          <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{error}</p>
          </div>
        ) : (
          ''
        )}

        <div>
          <label htmlFor="email" className="text-sm font-semibold text-beachvision-primary-light">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className={inputClass}
            placeholder="name@email.com"
          />
        </div>

        <div>
          <label htmlFor="code" className="text-sm font-semibold text-beachvision-primary-light">
            Invite code
          </label>
          <input
            id="code"
            name="code"
            type="text"
            autoComplete="off"
            required
            className={inputClass}
            placeholder="Enter your invite code"
          />
        </div>

        <div>
          <label htmlFor="password" className="text-sm font-semibold text-beachvision-primary-light">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            className={inputClass}
            placeholder="Create a strong password"
          />
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="text-sm font-semibold text-beachvision-primary-light"
          >
            Confirm password
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            required
            className={inputClass}
            placeholder="Re-enter your password"
          />
        </div>

        <button
          type="submit"
          className="cursor-pointer mt-1 w-full rounded-xl bg-beachvision-cta px-4 py-3 font-display font-black uppercase tracking-wide text-beachvision-on-coral shadow-lg shadow-beachvision-coral/20 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={loading}
        >
          <span className="inline-flex items-center gap-2">
            {loading ? 'Creating account...' : 'Create account'}
            {!loading && <ArrowRight className="h-4 w-4" />}
          </span>
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-beachvision-muted">
        Already have an account?{' '}
        <Link to="/login/" className="font-semibold text-beachvision-teal hover:text-beachvision-primary-light">
          Sign in
        </Link>
      </p>
    </LayoutAuth>
  );
};

export default Register;
